import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  MapPin, Trophy, Users, Calendar, Flame, Palette, Award, Sparkles, Building2, Mic, Store,
  Rocket, Flag, Heart, Zap, Pencil, Loader2, Share2,
} from "lucide-react";
import api, { fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader, EmptyState, PassionBadge, PrimaryButton, GhostButton, PASSION_LABEL } from "@/components/ui-bits";
import { CommunityCard, ProjectCard } from "@/components/cards";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import ImageUpload from "@/components/ImageUpload";
import { toast } from "sonner";

const ACH_ICON = { users: Users, building: Building2, flame: Flame, palette: Palette, calendar: Calendar, mic: Mic, store: Store, trophy: Trophy };
const JOURNEY_ICON = { start: Rocket, join: Users, participate: Flag, create: Palette, build: Building2, achievement: Award, opportunity: Store };
const JOURNEY_COLOR = { start: "#FF6B00", join: "#55708F", participate: "#8FAF9B", create: "#FF6B00", build: "#071126", achievement: "#FFC94A", opportunity: "#8FAF9B" };

function EditProfile({ user, onSaved }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: user.name, bio: user.bio || "", location: user.location || "", avatar: user.avatar || "", cover: user.cover || "" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { const { data } = await api.put("/users/me", form); onSaved(data); setOpen(false); toast.success("Profile updated"); }
    catch { toast.error("Could not save"); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><GhostButton data-testid="edit-profile-btn"><Pencil className="h-4 w-4" />Edit profile</GhostButton></DialogTrigger>
      <DialogContent className="rounded-2xl max-h-[90vh] overflow-y-auto" data-testid="edit-profile-dialog">
        <DialogHeader><DialogTitle>Edit your profile</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div><p className="text-sm font-medium mb-1.5">Cover image</p><ImageUpload value={form.cover} onChange={(v) => setForm({ ...form, cover: v })} aspect="aspect-[3/1]" label="Cover" testid="cover-upload" /></div>
          <div className="w-28"><p className="text-sm font-medium mb-1.5">Avatar</p><ImageUpload value={form.avatar} onChange={(v) => setForm({ ...form, avatar: v })} aspect="aspect-square" label="Photo" testid="avatar-upload" /></div>
          <Field label="Name"><input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="pa-in-field" data-testid="edit-name" /></Field>
          <Field label="Bio"><textarea value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} rows={3} className="pa-in-field resize-none" data-testid="edit-bio" /></Field>
          <Field label="Location"><input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="pa-in-field" data-testid="edit-location" /></Field>
          <PrimaryButton onClick={save} disabled={busy} className="w-full" data-testid="save-profile-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save changes"}</PrimaryButton>
        </div>
        <style>{`.pa-in-field{width:100%;padding:0.6rem 0.8rem;border-radius:0.7rem;border:1px solid rgba(7,17,38,0.12);font-size:0.9rem;outline:none}.pa-in-field:focus{border-color:rgba(255,107,0,0.5)}`}</style>
      </DialogContent>
    </Dialog>
  );
}
const Field = ({ label, children }) => <label className="block"><span className="text-sm font-medium text-[#071126]/80 mb-1 block">{label}</span>{children}</label>;

function ShareButton({ userId, name }) {
  const share = async () => {
    const url = `${window.location.origin}/u/${userId}`;
    const data = { title: `${name} on PassionAte`, text: `Check out ${name}'s passion journey on PassionAte`, url };
    try {
      if (navigator.share) { await navigator.share(data); return; }
      await navigator.clipboard.writeText(url);
      toast.success("Profile link copied to clipboard");
    } catch (e) {
      if (e?.name !== "AbortError") {
        try { await navigator.clipboard.writeText(url); toast.success("Profile link copied to clipboard"); }
        catch { toast.error("Could not share profile"); }
      }
    }
  };
  return <GhostButton onClick={share} data-testid="share-profile-btn"><Share2 className="h-4 w-4" />Share</GhostButton>;
}

export default function ProfileView({ userId }) {
  const { user: me, updateUser } = useAuth();
  const [data, setData] = useState(null);
  const isMe = !userId || userId === me?.id;
  const targetId = userId || me?.id;

  useEffect(() => { setData(null); api.get(`/users/${targetId}`).then((r) => setData(r.data)); }, [targetId]);
  if (!data) return <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8"><Loader /></div>;
  const u = data.user;

  const stats = [
    { icon: Users, v: data.stats.communities, l: "Communities" },
    { icon: Calendar, v: data.stats.events_attended, l: "Events" },
    { icon: Flame, v: data.stats.challenges, l: "Challenges" },
    { icon: Palette, v: data.stats.projects, l: "Projects" },
    { icon: Trophy, v: data.stats.achievements, l: "Achievements" },
    { icon: Zap, v: data.stats.passion_points, l: "Passion Points" },
  ];

  return (
    <div>
      <div className="h-40 bg-[#071126] relative overflow-hidden" data-testid="profile-cover">
        {u.cover ? (
          <>
            <img src={fileUrl(u.cover)} alt="" className="absolute inset-0 h-full w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#071126]/55 to-[#071126]/10" />
          </>
        ) : (
          <div className="absolute inset-0" style={{ background: "radial-gradient(circle at 20% 30%, rgba(255,107,0,0.35), transparent 45%), radial-gradient(circle at 80% 20%, rgba(255,201,74,0.25), transparent 40%)" }} />
        )}
      </div>
      <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="-mt-10 flex flex-col sm:flex-row sm:items-end gap-4 pb-2">
          <img src={fileUrl(u.avatar)} alt={u.name} className="h-28 w-28 rounded-2xl object-cover border-4 border-[#FFFDF8] shadow-lg" />
          <div className="flex-1 pb-1">
            <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-[#071126]">{u.name}</h1>
            {u.location && <p className="text-sm text-[#55708F] inline-flex items-center gap-1 mt-1"><MapPin className="h-3.5 w-3.5" />{u.location}</p>}
          </div>
          <div className="flex items-center gap-2">
            <ShareButton userId={u.id} name={u.name} />
            {isMe && <EditProfile user={u} onSaved={(nu) => { setData((d) => ({ ...d, user: { ...d.user, ...nu } })); updateUser(nu); }} />}
          </div>
        </div>
        {u.bio && <p className="text-[#071126]/70 mt-3 max-w-2xl">{u.bio}</p>}
        {u.passions?.length > 0 && <div className="flex flex-wrap gap-2 mt-3">{u.passions.map((p) => <PassionBadge key={p} slug={p} />)}</div>}

        <div className="grid grid-cols-3 sm:grid-cols-6 gap-3 mt-8">
          {stats.map((s) => (
            <div key={s.l} className="pa-card p-4 text-center" data-testid={`stat-${s.l.toLowerCase().replace(" ", "-")}`}>
              <s.icon className="h-4.5 w-4.5 text-[#FF6B00] mx-auto" />
              <p className="text-xl font-bold text-[#071126] mt-1">{s.v}</p>
              <p className="text-[10px] text-[#55708F] font-medium uppercase tracking-wide">{s.l}</p>
            </div>
          ))}
        </div>

        {/* Passion Journey */}
        <section className="mt-12">
          <div className="flex items-center gap-2 mb-6"><Sparkles className="h-5 w-5 text-[#FF6B00]" /><h2 className="text-2xl font-bold tracking-tight text-[#071126]">Passion Journey</h2></div>
          {data.journey?.length ? (
            <div className="relative pl-8">
              <div className="absolute left-3 top-2 bottom-2 w-0.5 bg-[#071126]/10" />
              <div className="space-y-5">
                {data.journey.map((j, i) => {
                  const Icon = JOURNEY_ICON[j.kind] || Flag;
                  const color = JOURNEY_COLOR[j.kind] || "#55708F";
                  return (
                    <div key={j.id} className="relative pa-in" style={{ animationDelay: `${i * 0.04}s` }} data-testid={`journey-${i}`}>
                      <span className="absolute -left-8 h-6 w-6 rounded-full border-2 border-[#FFFDF8] grid place-items-center" style={{ background: color }}><Icon className="h-3 w-3 text-white" /></span>
                      <div className="pa-card p-4"><p className="text-[#071126] font-medium text-sm">{j.title}</p></div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : <EmptyState icon={Rocket} title="The journey begins" hint={isMe ? "Join a community or take on a challenge to start your passion journey." : "This member is just getting started."} />}
        </section>

        {/* Achievements */}
        {data.achievements?.length > 0 && (
          <section className="mt-12">
            <h2 className="text-2xl font-bold tracking-tight text-[#071126] mb-5">Achievements</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {data.achievements.map((a) => {
                const Icon = ACH_ICON[a.icon] || Award;
                return (
                  <div key={a.id} className="pa-card p-5 text-center pa-pop" data-testid={`achievement-${a.key}`}>
                    <span className="h-12 w-12 rounded-2xl bg-[#FFC94A]/25 grid place-items-center mx-auto"><Icon className="h-6 w-6 text-[#c25400]" /></span>
                    <p className="font-semibold text-[#071126] mt-3 text-sm">{a.title}</p>
                    <p className="text-xs text-[#071126]/55 mt-0.5">{a.desc}</p>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* Built communities */}
        {data.built_communities?.length > 0 && (
          <section className="mt-12">
            <h2 className="text-2xl font-bold tracking-tight text-[#071126] mb-5">Communities built</h2>
            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">{data.built_communities.map((c) => <CommunityCard key={c.id} c={c} />)}</div>
          </section>
        )}

        {/* Projects */}
        <section className="mt-12 pb-8">
          <h2 className="text-2xl font-bold tracking-tight text-[#071126] mb-5">Creations</h2>
          {data.projects?.length ? <div className="grid gap-5 grid-cols-2 lg:grid-cols-4">{data.projects.map((p) => <ProjectCard key={p.id} p={p} />)}</div>
            : <EmptyState icon={Palette} title="No projects yet" hint={isMe ? "Start documenting your passion." : "Nothing here yet."} action={isMe && <PrimaryButton as={Link} to="/create/project">Add a project</PrimaryButton>} />}
        </section>
      </div>
    </div>
  );
}
